import * as Y from "yjs";
import type {
  BlockContent,
  BlockNode,
  EdgeType,
  EntityEdge,
  EntityNode,
  GraphEdge,
  SpatialTransform,
} from "./graph-types";

function uuidv4() {
  return crypto.randomUUID();
}

function writeMapValues(target: Y.Map<unknown>, data: Record<string, unknown>) {
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) {
      target.delete(key);
    } else {
      target.set(key, value);
    }
  });
}

function normalizeLegacyBlockShape(data: BlockNode): BlockNode {
  const legacy = data as Partial<EntityNode>;
  if (!data.spatialTransform && legacy.position) {
    return {
      ...data,
      spatialTransform: {
        position: legacy.position,
      },
    };
  }
  return data;
}

const DEFAULT_ENCOUNTER_PROPERTIES = {
  status: "preparing",
  round: 0,
  currentTurnIndex: 0,
  turnOrder: "",
};

type EncounterTurnEntry = {
  blockId: string;
  initiative: number;
  name: string;
};

function getNodeName(nodesMap: Y.Map<unknown>, nodeId: string): string {
  const node = nodesMap.get(nodeId) as Y.Map<unknown> | undefined;
  const rawName = node?.get("name");
  return typeof rawName === "string" && rawName.trim() ? rawName : nodeId;
}

function parseFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && Number.isFinite(Number(value)))
    return Number(value);
  return 0;
}

function parseTurnOrder(raw: unknown): EncounterTurnEntry[] {
  if (typeof raw !== "string" || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((entry: any) => {
        if (
          !entry ||
          typeof entry.blockId !== "string" ||
          !entry.blockId.trim()
        ) {
          return null;
        }
        if (typeof entry.name !== "string") return null;
        return {
          blockId: entry.blockId,
          initiative: parseFiniteNumber(entry.initiative),
          name: entry.name,
        } as EncounterTurnEntry;
      })
      .filter((entry): entry is EncounterTurnEntry => entry !== null)
      .sort((a, b) => b.initiative - a.initiative);
  } catch {
    return [];
  }
}

function readEncounterProperties(
  nodesMap: Y.Map<unknown>,
  encounterId: string,
): { [key: string]: unknown } | null {
  const encounter = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounter) return null;
  return (
    (encounter.get("properties") as { [key: string]: unknown } | undefined) ||
    {}
  );
}

function writeEncounterProperties(
  nodesMap: Y.Map<unknown>,
  encounterId: string,
  updates: Record<string, unknown>,
) {
  const encounter = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounter) return;

  const nextProperties = {
    ...((encounter.get("properties") as Record<string, unknown>) ?? {}),
    ...updates,
  };
  encounter.set("properties", nextProperties);
}

function normalizeTurnIndex(index: number, participantCount: number): number {
  if (participantCount <= 0) return 0;
  const max = participantCount - 1;
  const normalized =
    ((index % participantCount) + participantCount) % participantCount;
  return Math.min(Math.max(0, normalized), max);
}

/** Creates an encounter metadata block and links it under the scene node. */
export function createEncounter(doc: Y.Doc, sceneId: string) {
  const nodesMap = doc.getMap("nodes");
  const sceneMap = nodesMap.get(sceneId) as Y.Map<unknown> | undefined;

  return appendBlock(
    doc,
    {
      type: "encounter",
      name: "Encounter",
      properties: {
        ...DEFAULT_ENCOUNTER_PROPERTIES,
        status: "preparing",
      },
    },
    sceneMap ? { parentId: sceneId, edgeType: "hierarchy" } : undefined,
  );
}

/** Adds or updates a participant in an encounter turn order. */
export function addToEncounter(
  doc: Y.Doc,
  encounterId: string,
  blockId: string,
  initiative = 0,
) {
  const nodesMap = doc.getMap("nodes");
  const encounterNode = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounterNode || encounterNode.get("type") !== "encounter") return;

  const properties = readEncounterProperties(nodesMap, encounterId) ?? {};
  const current = parseTurnOrder(properties.turnOrder);
  const name = getNodeName(nodesMap, blockId);
  const nextList = [
    ...current.filter((entry) => entry.blockId !== blockId),
    { blockId, initiative: parseFiniteNumber(initiative), name },
  ]
    .sort((a, b) => b.initiative - a.initiative)
    .map((entry) => ({ ...entry }));

  writeEncounterProperties(nodesMap, encounterId, {
    turnOrder: JSON.stringify(nextList),
  });
}

/** Removes a participant from an encounter turn order. */
export function removeFromEncounter(
  doc: Y.Doc,
  encounterId: string,
  blockId: string,
) {
  const nodesMap = doc.getMap("nodes");
  const encounterNode = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounterNode || encounterNode.get("type") !== "encounter") return;

  const properties = readEncounterProperties(nodesMap, encounterId) ?? {};
  const turnOrder = parseTurnOrder(properties.turnOrder);
  const removedIndex = turnOrder.findIndex(
    (entry) => entry.blockId === blockId,
  );
  const next = turnOrder.filter((entry) => entry.blockId !== blockId);

  const currentTurnIndexRaw = parseFiniteNumber(properties.currentTurnIndex);
  const currentTurnIndex = normalizeTurnIndex(
    currentTurnIndexRaw,
    Math.max(next.length, 1),
  );

  if (next.length === 0) {
    writeEncounterProperties(nodesMap, encounterId, {
      turnOrder: "[]",
      currentTurnIndex: 0,
    });
    return;
  }

  let nextTurnIndex = currentTurnIndex;
  if (removedIndex >= 0) {
    if (removedIndex === currentTurnIndex) {
      nextTurnIndex = Math.min(currentTurnIndex, next.length - 1);
    } else if (removedIndex < currentTurnIndex) {
      nextTurnIndex = Math.max(0, currentTurnIndex - 1);
    }
  }

  writeEncounterProperties(nodesMap, encounterId, {
    turnOrder: JSON.stringify(next),
    currentTurnIndex: nextTurnIndex,
  });
}

/** Starts an encounter and initializes round counter. */
export function startEncounter(doc: Y.Doc, encounterId: string) {
  const nodesMap = doc.getMap("nodes");
  const encounter = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounter || encounter.get("type") !== "encounter") return;

  writeEncounterProperties(nodesMap, encounterId, {
    status: "active",
    round: 1,
    currentTurnIndex: 0,
  });
}

/** Moves encounter turn to next participant and increments round on wrap. */
export function nextTurn(doc: Y.Doc, encounterId: string) {
  const nodesMap = doc.getMap("nodes");
  const encounter = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounter || encounter.get("type") !== "encounter") return;

  const properties = readEncounterProperties(nodesMap, encounterId) ?? {};
  const turnOrder = parseTurnOrder(properties.turnOrder);
  if (turnOrder.length === 0) return;

  const currentTurnIndex = normalizeTurnIndex(
    parseFiniteNumber(properties.currentTurnIndex),
    turnOrder.length,
  );
  const nextTurnIndex = normalizeTurnIndex(
    currentTurnIndex + 1,
    turnOrder.length,
  );
  const wrapped = currentTurnIndex + 1 >= turnOrder.length;
  const round = parseFiniteNumber(properties.round);
  const nextRound = wrapped ? round + 1 : round;

  writeEncounterProperties(nodesMap, encounterId, {
    currentTurnIndex: nextTurnIndex,
    round: nextRound,
  });
}

/** Ends an active encounter. */
export function endEncounter(doc: Y.Doc, encounterId: string) {
  const nodesMap = doc.getMap("nodes");
  const encounter = nodesMap.get(encounterId) as Y.Map<unknown> | undefined;
  if (!encounter || encounter.get("type") !== "encounter") return;

  writeEncounterProperties(nodesMap, encounterId, {
    status: "ended",
  });
}

/**
 * Appends a universal block node to the graph.
 * Optionally links it in document hierarchy using a parent edge.
 */
export function appendBlock(
  doc: Y.Doc,
  data: BlockNode,
  options?: {
    parentId?: string;
    edgeType?: EdgeType;
    edgePayload?: Record<string, unknown>;
    order?: number;
  },
): string {
  const id = uuidv4();
  const nodesMap = doc.getMap("nodes");

  const normalized = normalizeLegacyBlockShape(data);
  const nodeMap = new Y.Map<unknown>();
  writeMapValues(nodeMap, {
    ...normalized,
    id,
    // Keep legacy projection to avoid immediate renderer breakage.
    position:
      (normalized as Partial<EntityNode>).position ??
      normalized.spatialTransform?.position,
  });

  doc.transact(() => {
    nodesMap.set(id, nodeMap);
    if (options?.parentId) {
      createEdge(doc, {
        source_id: options.parentId,
        target_id: id,
        type: options.edgeType ?? "hierarchy",
        payload: options.edgePayload,
        order: options.order,
      });
    }
  });

  return id;
}

/** Updates top-level properties of a block node. */
export function updateBlock(doc: Y.Doc, id: string, data: Partial<BlockNode>) {
  const nodesMap = doc.getMap("nodes");
  const nodeMap = nodesMap.get(id) as Y.Map<unknown> | undefined;
  if (!nodeMap) return;

  const normalized = normalizeLegacyBlockShape(data as BlockNode);
  const patch = {
    ...normalized,
  } as Record<string, unknown>;

  if (normalized.spatialTransform?.position) {
    patch.position = normalized.spatialTransform.position;
  }

  writeMapValues(nodeMap, patch);
}

/** Updates only the content payload of a block node. */
export function updateBlockContent(
  doc: Y.Doc,
  id: string,
  content: BlockContent,
) {
  const nodesMap = doc.getMap("nodes");
  const nodeMap = nodesMap.get(id) as Y.Map<unknown> | undefined;
  if (!nodeMap) return;
  nodeMap.set("content", content);
}

/** Promotes a block to spatial representation by setting `spatialTransform`. */
export function promoteToSpatial(
  doc: Y.Doc,
  id: string,
  transform: SpatialTransform,
) {
  const nodesMap = doc.getMap("nodes");
  const nodeMap = nodesMap.get(id) as Y.Map<unknown> | undefined;
  if (!nodeMap) return;

  nodeMap.set("spatialTransform", transform);
  // Backward-compat for existing renderer path.
  nodeMap.set("position", transform.position);
}

/** Demotes a block from spatial view by removing `spatialTransform`. */
export function demoteFromSpatial(doc: Y.Doc, id: string) {
  const nodesMap = doc.getMap("nodes");
  const nodeMap = nodesMap.get(id) as Y.Map<unknown> | undefined;
  if (!nodeMap) return;

  nodeMap.delete("spatialTransform");
  // Remove legacy projection too so demotion reliably hides the block from canvas.
  nodeMap.delete("position");
}

/** Creates a node in the graph. Returns the generated UUID. */
export function createNode(doc: Y.Doc, data: EntityNode): string {
  return appendBlock(doc, {
    ...data,
    spatialTransform: data.spatialTransform ?? { position: data.position },
  });
}

/** Updates top-level properties of a specific node. */
export function updateNode(doc: Y.Doc, id: string, data: Partial<EntityNode>) {
  const patch: Partial<BlockNode> = {
    ...data,
  };

  if (data.position) {
    patch.spatialTransform = {
      position: data.position,
      ...data.spatialTransform,
    };
  }

  updateBlock(doc, id, patch);
}

/** Deletes a node and sweeps any dangling edges referencing it inside a transaction. */
export function deleteNode(doc: Y.Doc, id: string) {
  const nodesMap = doc.getMap("nodes");
  // Find dangling edges
  const edgesMap = doc.getMap("edges");
  const edgesToDelete: string[] = [];

  for (const [edgeId, edgeMap] of edgesMap.entries()) {
    const e = edgeMap as Y.Map<unknown>;
    if (e.get("source_id") === id || e.get("target_id") === id) {
      edgesToDelete.push(edgeId);
    }
  }

  doc.transact(() => {
    nodesMap.delete(id);
    for (const edgeId of edgesToDelete) {
      edgesMap.delete(edgeId);
    }
  });
}

/** Connects two nodes together with a directional edge. */
export function createEdge(doc: Y.Doc, edge: GraphEdge | EntityEdge): string {
  const id = uuidv4();
  const edgesMap = doc.getMap("edges");

  const edgeMap = new Y.Map<unknown>();
  Object.entries(edge).forEach(([key, value]) => {
    if (value !== undefined) {
      edgeMap.set(key, value);
    }
  });

  edgesMap.set(id, edgeMap);
  return id;
}

/** Deletes an edge strictly. */
export function deleteEdge(doc: Y.Doc, id: string) {
  const edgesMap = doc.getMap("edges");
  edgesMap.delete(id);
}

// End of core graph primitives
